/**
 * Rating column symbols.
 *
 * monday’s rating column settings carry the column's own
 * configuration, per the API reference:
 *   { "limit": 5, "symbol": "stars", "color": "#fdab3d" }
 *   limit  — 3..10 symbols
 *   symbol — "stars" | "hearts"
 *   color  — hex colour of a filled symbol
 * https://developer.monday.com/api-reference/reference/rating
 *
 * So a rating cell must render THIS column's symbol, count and colour — not a
 * hardcoded 5 stars. Keys are read defensively (aliases + clamping) in case
 * older boards spell them differently.
 *
 * Vibe's Favorite and Heart icons are outline-only, so they can't show a
 * filled symbol in the column's colour. The glyphs stay inline SVG paths.
 * Everything else in the editor is Vibe.
 */
import React from 'react';

export const RATING_DEFAULTS = { limit: 5, symbol: 'stars', color: '#fdab3d' };

const PATHS = {
  stars:
    'M12 2.6l2.9 5.9 6.5.9-4.7 4.6 1.1 6.5L12 17.4l-5.8 3.1 1.1-6.5L2.6 9.4l6.5-.9z',
  hearts:
    'M12 21s-7.5-4.7-9.3-9.1C1.4 8.6 3.1 5 6.6 5c2.2 0 3.7 1.2 4.5 2.3l.9 1.2.9-1.2C13.7 6.2 15.2 5 17.4 5c3.5 0 5.2 3.6 3.9 6.9C19.5 16.3 12 21 12 21z',
};

/** Normalize a rating column's settings, tolerating alias keys and bad values. */
export function ratingSettings(column) {
  const s = (column && column.settings) || {};
  const rawLimit = s.limit ?? s.max ?? s.max_rating ?? s.count;
  const limit = Math.max(1, Math.min(10, Number(rawLimit) || RATING_DEFAULTS.limit));
  const rawSymbol = String(s.symbol ?? s.symbol_name ?? s.icon ?? RATING_DEFAULTS.symbol).toLowerCase();
  const symbol = PATHS[rawSymbol]
    ? rawSymbol
    : rawSymbol.indexOf('heart') === 0
      ? 'hearts'
      : RATING_DEFAULTS.symbol;
  const color = s.color || RATING_DEFAULTS.color;
  return { limit, symbol, color };
}

export function RatingSymbol({ symbol = 'stars', filled, color, size = 14 }) {
  const path = PATHS[symbol] || PATHS.stars;
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      aria-hidden="true"
      focusable="false"
      style={{ display: 'block', flex: 'none' }}
    >
      <path
        d={path}
        fill={filled ? color : 'none'}
        stroke={filled ? color : 'currentColor'}
        strokeOpacity={filled ? 1 : 0.35}
        strokeWidth="1.6"
        strokeLinejoin="round"
      />
    </svg>
  );
}

/** Read-only row of symbols — `value` is the rating (1..limit) or null. */
export function RatingDisplay({ column, value, size = 14 }) {
  const { limit, symbol, color } = ratingSettings(column);
  return (
    <span className="spt-rating" role="img" aria-label={`${value || 0} out of ${limit}`}>
      {Array.from({ length: limit }, (_, i) => (
        <RatingSymbol
          key={i}
          symbol={symbol}
          color={color}
          size={size}
          filled={!!value && i < value}
        />
      ))}
    </span>
  );
}
